import { useEffect, useId, useRef } from 'react';
import { useReducedMotion } from '@mantine/hooks';

import { useLogoMotionPaused } from './logoMotion';

/** Dispatch on `window` to make every mounted wordmark star twinkle once (e.g. when a demo finds an answer). */
export const LOGO_TWINKLE_EVENT = 'chatco:logo-twinkle';

const STAR = '#d3a641';
const STAR_PEAK = '#f8e3a1';

const between = (min: number, max: number) => min + Math.random() * (max - min);

interface ChatcoWordmarkProps {
  /** Rendered height in px; width follows the 219:54 artwork ratio. */
  height?: number;
  /** Random twinkle of the gold star. Off for static placements. */
  twinkle?: boolean;
  className?: string;
}

/**
 * The official inverted Chat.co lockup (white wordmark, gold star) as inline vector, traced from the
 * brand PDF, so the star can animate on its own. For dark backgrounds only.
 */
const ChatcoWordmark: React.FC<ChatcoWordmarkProps> = ({
  height = 32,
  twinkle = true,
  className,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const starRef = useRef<SVGGElement>(null);
  const fillRef = useRef<SVGPathElement>(null);
  const glowRef = useRef<SVGCircleElement>(null);
  const reducedMotion = useReducedMotion();
  // Shared state, so a pause set before this wordmark mounted (or before its effect restarted) holds.
  const motionPaused = useLogoMotionPaused();

  useEffect(() => {
    // Paused or reduced motion: nothing is scheduled at all; cleanup of the previous run cancels any
    // twinkle already in flight.
    if (!twinkle || reducedMotion || motionPaused) return undefined;
    const star = starRef.current;
    const fill = fillRef.current;
    const glow = glowRef.current;
    if (!star || !fill || !glow || typeof star.animate !== 'function') return undefined;

    const running = new Set<Animation>();
    const track = (animation: Animation) => {
      running.add(animation);
      animation.addEventListener('finish', () => running.delete(animation));
      animation.addEventListener('cancel', () => running.delete(animation));
    };
    const stopRunning = () => Array.from(running).forEach((animation) => animation.cancel());

    const flash = (strength: number) => {
      const duration = between(900, 1400);
      const peak = between(0.32, 0.45);
      const tilt = between(-9, 9) * strength;
      const easing = 'cubic-bezier(.3,.6,.3,1)';
      track(
        star.animate(
          [
            { transform: 'scale(1) rotate(0deg)' },
            { transform: `scale(${1 + 0.2 * strength}) rotate(${tilt}deg)`, offset: peak },
            { transform: 'scale(1) rotate(0deg)' },
          ],
          { duration, easing }
        )
      );
      track(
        fill.animate([{ fill: STAR }, { fill: STAR_PEAK, offset: peak }, { fill: STAR }], {
          duration,
          easing,
        })
      );
      track(
        glow.animate(
          [
            { opacity: 0, transform: 'scale(.55)' },
            { opacity: 0.85 * strength, transform: 'scale(1.1)', offset: peak },
            { opacity: 0, transform: 'scale(.8)' },
          ],
          { duration: duration * 1.15, easing }
        )
      );
    };

    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (fn: () => void, ms: number) => {
      const t = setTimeout(() => {
        timers.delete(t);
        fn();
      }, ms);
      timers.add(t);
    };
    // Irregular rhythm: every 2.6–7.2s, and roughly one time in three a softer second glint.
    const schedule = () =>
      later(
        () => {
          if (!document.hidden) {
            flash(between(0.6, 1));
            if (Math.random() < 0.3) later(() => flash(between(0.35, 0.6)), between(260, 420));
          }
          schedule();
        },
        between(2600, 7200)
      );
    schedule();

    const onSignal = () => flash(0.7);
    window.addEventListener(LOGO_TWINKLE_EVENT, onSignal);
    return () => {
      timers.forEach(clearTimeout);
      stopRunning();
      window.removeEventListener(LOGO_TWINKLE_EVENT, onSignal);
    };
  }, [twinkle, reducedMotion, motionPaused]);

  const clipA = `${uid}-clip-a`;
  const clipB = `${uid}-clip-b`;
  const glowId = `${uid}-glow`;
  const flipped = 'matrix(1,0,0,-1,0,54.2422)';
  const centered = { transformBox: 'fill-box', transformOrigin: 'center' } as React.CSSProperties;

  return (
    <svg
      viewBox="0 0 219.183 54.2422"
      height={height}
      width={(height * 219.183) / 54.2422}
      role="img"
      aria-label="Chat.co"
      className={className}
      style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <clipPath id={clipA}>
          <path
            transform={flipped}
            d="M193.966 22.548C194.311 21.744 194.763 20.989 195.317 20.298L197.32 19.812 196.889 21.694C196.306 22.441 195.873 23.279 195.601 24.181Z"
          />
        </clipPath>
        <clipPath id={clipB}>
          <path
            transform={flipped}
            d="M198.951 18.374 199.185 16.66C199.768 15.912 200.202 15.075 200.475 14.173L202.108 15.807C201.765 16.61 201.313 17.365 200.758 18.056Z"
          />
        </clipPath>
        <radialGradient id={glowId}>
          <stop offset="0" stopColor="#f6dd8f" stopOpacity=".9" />
          <stop offset=".45" stopColor={STAR} stopOpacity=".35" />
          <stop offset="1" stopColor={STAR} stopOpacity="0" />
        </radialGradient>
      </defs>
      <path
        transform="matrix(1,0,0,-1,121.3398,18.724598)"
        fill="#ffffff"
        fillRule="evenodd"
        d="M0 0V-17.131C0-24.393 2.784-31.047 10.463-32.763L10.638-32.802V-34.927L10.377-34.883C1.254-33.339-2.271-25.937-2.271-17.35V11.881 12.104H0V2.053H10.638V0ZM-10.342-24.908C-13.54-31.118-19.916-35.286-26.955-35.286-37.167-35.507-46.142-26.539-45.921-16.329-46.142-6.115-37.177 2.861-26.965 2.64-16.748 2.861-7.849-6.116-8.069-16.32V-34.698H-10.342ZM-10.268-16.323C-10.268-7.069-17.547 .587-26.885 .587-36.203 .587-43.649-7.047-43.649-16.323-43.649-25.526-36.127-33.233-26.885-33.233-17.623-33.233-10.268-25.504-10.268-16.323M-79.962-5.392C-76.95 .458-72.561 2.64-66.06 2.64-54.953 2.64-49.885-4.906-49.885-15.369V-34.698H-52.157V-15.369C-52.157-6.473-56.467 .587-66.06 .587-75.359 .587-79.962-6.076-79.962-14.856V-34.698H-82.234V16.725H-79.962ZM-86.388-26.608C-89.92-32.031-95.897-35.286-102.368-35.286-112.581-35.507-121.556-26.539-121.335-16.329-121.556-6.115-112.588 2.861-102.376 2.64-96.128 2.64-90.116-.408-86.682-5.67L-86.458-6.015H-89.04L-89.106-5.925C-92.194-1.758-97.136 .587-102.299 .587-111.617 .587-119.063-7.047-119.063-16.323-119.063-25.526-111.542-33.233-102.299-33.233-96.969-33.233-91.962-30.731-88.885-26.358L-88.819-26.265H-86.164ZM15.638-31.798C16.559-31.798 17.306-32.545 17.306-33.466 17.306-34.386 16.559-35.133 15.638-35.133 14.717-35.133 13.971-34.386 13.971-33.466 13.971-32.545 14.717-31.798 15.638-31.798M83.408-.688C81.164-.558 78.942-1.07 77.141-2.462 75.942-3.388 75.027-4.634 74.484-6.046 73.955-7.425 73.794-8.937 74.04-10.393 74.255-11.657 74.769-12.822 75.549-13.824L76.698-5.171 77.865-13.958 81.168-11.868 79.08-15.173 87.866-16.341 79.419-17.463C80.461-18.763 81.144-20.287 81.424-21.939 81.734-23.766 81.53-25.657 80.867-27.384 80.186-29.156 79.039-30.719 77.537-31.88 76.743-32.493 75.873-32.979 74.945-33.339 75.527-33.398 76.112-33.428 76.698-33.428H76.802C77.051-33.426 77.271-33.516 77.449-33.691 77.626-33.867 77.719-34.088 77.719-34.338V-34.606C77.719-35.106 77.313-35.514 76.814-35.517 76.775-35.517 76.736-35.518 76.698-35.518 67.544-35.518 59.652-29.022 57.906-20.049 57.77-19.347 57.673-18.637 57.615-17.924 53.804-27.471 49.878-35.286 37.97-35.286 27.757-35.507 18.782-26.539 19.002-16.329 18.782-6.115 27.749 2.861 37.961 2.64 44.209 2.64 50.222-.408 53.655-5.67L53.88-6.015H51.298L51.231-5.925C48.143-1.758 43.202 .587 38.038 .587 28.721 .587 21.274-7.047 21.274-16.323 21.274-25.526 28.796-33.233 38.038-33.233 59.672-33.233 52.195-.742 73.173 2.507 73.442 2.549 73.697 2.475 73.904 2.299 74.112 2.122 74.224 1.88 74.224 1.606V1.329C74.224 .879 73.901 .501 73.457 .431 65.632-.821 59.643-8.583 59.643-16.372 59.643-23.134 63.633-29.252 69.817-31.981 72.113-32.148 74.41-31.646 76.255-30.22 77.453-29.293 78.368-28.048 78.911-26.635 79.44-25.257 79.602-23.744 79.355-22.288 79.141-21.024 78.627-19.86 77.847-18.857L76.698-27.51 75.531-18.724 72.227-20.812 74.315-17.508 65.528-16.341 73.978-15.219C72.935-13.919 72.252-12.395 71.971-10.741 71.661-8.916 71.865-7.023 72.529-5.297 73.21-3.525 74.356-1.963 75.859-.801 76.614-.217 77.441 .252 78.321 .607 77.782 .658 77.24 .683 76.698 .683 76.664 .683 76.629 .683 76.595 .683 76.346 .681 76.124 .771 75.946 .947 75.769 1.123 75.677 1.343 75.677 1.593V1.862C75.677 2.36 76.081 2.768 76.579 2.772 76.619 2.772 76.658 2.773 76.698 2.773 87.271 2.773 95.843-5.799 95.843-16.372 95.843-25.573 89.281-33.492 80.249-35.187 79.977-35.238 79.714-35.17 79.5-34.993 79.286-34.815 79.171-34.57 79.171-34.293V-34.02C79.171-33.579 79.479-33.208 79.911-33.125 87.935-31.596 93.753-24.55 93.753-16.372 93.753-9.534 89.674-3.372 83.408-.688"
      />
      {/* Two sub-pixel shading pieces where the S strokes cross the star, as exported from the brand file. */}
      <g clipPath={`url(#${clipA})`}>
        <image
          x="193"
          y="30"
          width="5"
          height="5"
          href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAATUlEQVR4nGP4jwY+PT3xnwHG+fb25v+3d7b+f3KqDyL45eWF/y8vL/z/8Ejj/1tbE/8zvL296f+Tkz3/b29P+395qSMYM9w/UAHnwDAAkjhUIArxYIAAAAAASUVORK5CYII="
        />
      </g>
      <g clipPath={`url(#${clipB})`}>
        <image
          x="198"
          y="35"
          width="5"
          height="6"
          href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAGCAYAAAAL+1RLAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAARklEQVR4nGO4vNTxPzpmQBe4v78CVfDJie7/b29thAje3Z3//8XFef8/vzj3HwQYHhyo/P/29qb/397c+A8DDB+fHP2PDgDh+GDmYPV04AAAAABJRU5ErkJggg=="
        />
      </g>
      <circle
        ref={glowRef}
        cx="198.04"
        cy="35.07"
        r="11"
        fill={`url(#${glowId})`}
        opacity="0"
        style={centered}
      />
      {/* The star path carries its own position matrix, so the twinkle transform goes on this wrapper. */}
      <g ref={starRef} style={centered}>
        <path
          ref={fillRef}
          transform="matrix(1,0,0,-1,199.3457,36.374)"
          fill={STAR}
          fillRule="evenodd"
          d="M0 0 1.413 .187 9.86 1.309 1.074 2.477 3.162 5.781-.141 3.691-1.308 12.478-2.019 7.12-2.457 3.825-2.617 2.618-4.028 2.431-12.478 1.309-3.69 .142-5.779-3.162-2.475-1.074-1.308-9.86-.159-1.208Z"
        />
      </g>
    </svg>
  );
};

export default ChatcoWordmark;
